import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../utils/api/axiosInstance";
import { useUserAuth } from "./UserAuthContext";
import { useToast } from "./ToastContext";

const UserProfileContext = createContext(null);

export const useUserProfile = () => {
  const ctx = useContext(UserProfileContext);
  if (!ctx) {
    throw new Error("useUserProfile must be used within UserProfileProvider");
  }
  return ctx;
};

export const UserProfileProvider = ({ children }) => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser, userAuthChecked } = useUserAuth();
  const { showToast } = useToast();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [showFollowers, setShowFollowers] = useState(false);
  const [showFollowing, setShowFollowing] = useState(false);
  const [followersList, setFollowersList] = useState([]);
  const [followingList, setFollowingList] = useState([]);
  const [loadingFollowers, setLoadingFollowers] = useState(false);
  const [loadingFollowing, setLoadingFollowing] = useState(false);

  const currentUserId = currentUser?.id || currentUser?._id || null;

  const isOwnProfile = useMemo(
    () =>
      Boolean(
        currentUser &&
          (currentUser.id === userId || currentUser._id === userId),
      ),
    [currentUser, userId],
  );

  const showToastRef = useRef(showToast);
  const navigateRef = useRef(navigate);
  showToastRef.current = showToast;
  navigateRef.current = navigate;

  const prevRouteUserIdRef = useRef(userId);

  const fetchProfile = useCallback(async ({ showLoader = false } = {}) => {
    try {
      if (showLoader) setLoading(true);
      const res = await api.get(`/users/${userId}/profile`);
      setProfile(res.data);
      setIsFollowing(res.data.isFollowing || false);
    } catch (err) {
      console.error("Error fetching profile:", err);
      if (err.response?.status === 404) {
        showToastRef.current({
          message: err.response?.data?.message || "User not found",
          type: "error",
        });
        navigateRef.current("/");
      } else {
        showToastRef.current({
          message: err.response?.data?.message || "Failed to load profile",
          type: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!userAuthChecked || !userId) return;

    let cancelled = false;
    const userIdChanged = prevRouteUserIdRef.current !== userId;
    prevRouteUserIdRef.current = userId;

    const loadProfile = async () => {
      if (userIdChanged) {
        setProfile(null);
        setLoading(true);
      }

      try {
        const res = await api.get(`/users/${userId}/profile`);
        if (cancelled) return;
        setProfile(res.data);
        setIsFollowing(res.data.isFollowing || false);
      } catch (err) {
        if (cancelled) return;
        console.error("Error fetching profile:", err);
        if (err.response?.status === 404) {
          showToastRef.current({
            message: err.response?.data?.message || "User not found",
            type: "error",
          });
          navigateRef.current("/");
        } else {
          showToastRef.current({
            message: err.response?.data?.message || "Failed to load profile",
            type: "error",
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [userId, userAuthChecked, currentUserId]);

  const fetchFollowers = async () => {
    if (followersList.length > 0) {
      setShowFollowers(true);
      return;
    }
    try {
      setLoadingFollowers(true);
      const res = await api.get(`/users/${userId}/followers`);
      setFollowersList(res.data.followers || []);
      setShowFollowers(true);
    } catch (err) {
      showToast({
        message: err.response?.data?.message || "Failed to load followers",
        type: "error",
      });
    } finally {
      setLoadingFollowers(false);
    }
  };

  const fetchFollowing = async () => {
    if (followingList.length > 0) {
      setShowFollowing(true);
      return;
    }
    try {
      setLoadingFollowing(true);
      const res = await api.get(`/users/${userId}/following`);
      setFollowingList(res.data.following || []);
      setShowFollowing(true);
    } catch (err) {
      showToast({
        message: err.response?.data?.message || "Failed to load following",
        type: "error",
      });
    } finally {
      setLoadingFollowing(false);
    }
  };

  const handleFollow = async () => {
    if (!currentUser) {
      navigate("/login");
      return;
    }

    const previous = isFollowing;
    const next = !previous;
    setIsFollowing(next);
    setProfile((prev) => {
      if (!prev?.stats) return prev;
      const delta = next ? 1 : -1;
      return {
        ...prev,
        stats: {
          ...prev.stats,
          followers: Math.max(0, (prev.stats.followers || 0) + delta),
        },
      };
    });

    try {
      setFollowLoading(true);
      const res = await api.post(`/users/${userId}/follow`);
      if (
        typeof res.data.isFollowing === "boolean" &&
        res.data.isFollowing !== next
      ) {
        const serverNext = res.data.isFollowing;
        setIsFollowing(serverNext);
        setProfile((prev) => {
          if (!prev?.stats) return prev;
          const delta = serverNext ? 1 : -1;
          return {
            ...prev,
            stats: {
              ...prev.stats,
              followers: Math.max(0, (prev.stats.followers || 0) + delta),
            },
          };
        });
      }
      fetchProfile();
    } catch (err) {
      setIsFollowing(previous);
      setProfile((prev) => {
        if (!prev?.stats) return prev;
        const delta = previous ? 1 : -1;
        return {
          ...prev,
          stats: {
            ...prev.stats,
            followers: Math.max(0, (prev.stats.followers || 0) + delta),
          },
        };
      });
      showToast({
        message: err.response?.data?.message || "Failed to follow user",
        type: "error",
      });
    } finally {
      setFollowLoading(false);
    }
  };

  const publicProfileProps = useMemo(() => {
    if (!profile) return null;
    const { user, stats, isPrivate, hasPendingRequest } = profile;
    return {
      user,
      stats,
      trustIndicators: profile.trustIndicators,
      impact: profile.impact,
      isFollowing,
      hasPendingRequest: hasPendingRequest || false,
      isOwnProfile,
      isPrivate,
      currentUser,
      followLoading,
      onFollow: handleFollow,
      onFollowersClick: fetchFollowers,
      onFollowingClick: fetchFollowing,
      showFollowers,
      showFollowing,
      followersList,
      followingList,
      loadingFollowers,
      loadingFollowing,
      onCloseFollowers: () => setShowFollowers(false),
      onCloseFollowing: () => setShowFollowing(false),
      expertiseScores: profile.expertiseScores,
      publicLists: profile.publicLists,
      badges: profile.badges || [],
      shoppingStyle: profile.shoppingStyle,
    };
  }, [
    profile,
    isFollowing,
    isOwnProfile,
    currentUser,
    followLoading,
    showFollowers,
    showFollowing,
    followersList,
    followingList,
    loadingFollowers,
    loadingFollowing,
  ]);

  const value = {
    userId,
    profile,
    loading,
    isOwnProfile,
    publicProfileProps,
    refetchProfile: fetchProfile,
  };

  return (
    <UserProfileContext.Provider value={value}>
      {children}
    </UserProfileContext.Provider>
  );
};
